// crewbit-admin/src/hooks/useCreateScheduleShift.ts
//
// Schedule Module Phase B.1 — paired hook for `create_schedule_shift` RPC
// (Migration 312).
// Spec: docs/references/schedule/SCHEDULE_MODULE_WIRING_v1.md §4.6.
//
// Mirror of crewbit-mobile/hooks/useCreateScheduleShift.ts.

import { useCallback } from 'react';
import { callScheduleRpc, type ScheduleRpcResult } from '../lib/scheduleRpc';
import type { ConflictDetail } from '../lib/errorMap';

export type ScheduleShiftSource = 'manual' | 'template' | 'swap';

export interface CreateScheduleShiftInput {
  /** public.users.id the shift is for. */
  userId: string;
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM (24h). */
  startTime: string;
  /** HH:MM (24h). Same-day; cross-midnight rejected with INVALID_TIME_RANGE. */
  endTime: string;
  position?: string;
  location?: string;
  locationId?: string;
  templateShiftId?: string;
  notes?: string;
  source?: ScheduleShiftSource;
  idempotencyKey?: string;
  force?: boolean;
}

export type CreateScheduleShiftResult = ScheduleRpcResult<{
  success: true;
  shift_id: string;
  entry_id: string;
  conflicts?: ConflictDetail[];
  already_created?: boolean;
}>;

export function useCreateScheduleShift() {
  return useCallback(
    async (input: CreateScheduleShiftInput): Promise<CreateScheduleShiftResult> => {
      return callScheduleRpc<{
        success: true;
        shift_id: string;
        entry_id: string;
        conflicts?: ConflictDetail[];
        already_created?: boolean;
      }>('create_schedule_shift', {
        p_user_id: input.userId,
        p_date: input.date,
        p_start_time: input.startTime,
        p_end_time: input.endTime,
        p_position: input.position ?? null,
        p_location: input.location ?? null,
        p_location_id: input.locationId ?? null,
        p_template_shift_id: input.templateShiftId ?? null,
        p_notes: input.notes ?? null,
        p_source: input.source ?? 'manual',
        p_idempotency_key: input.idempotencyKey ?? null,
        p_force: input.force ?? false,
      });
    },
    [],
  );
}
